import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormTextarea from '../../../components/form/FormTextarea';

import {
  aiDispositionAdd,
  aiDispositionUpdate
} from '../../../store/slices/tenantSlices/settingSlice';
import { callexAiAgentOptionsGet } from '../../../store/slices/tenantSlices/configurationSlice';

const AddEditAIDispositionModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [botOptions, setBotOptions] = useState([]);

  const initialValues = {
    bot_id: '',
    name: '',
    description: ''
  };

  const validationSchema = Yup.object({
    bot_id: Yup.mixed().required('Bot is required'),
    name: Yup.string().trim().required('Name is required')
  });

  const onSubmit = async (values, { resetForm }) => {
    try {
      if (isEdit) {
        // PUT /bot-disposition/{id} accepts only name + description
        const payload = {
          id: editRecord.id,
          name: values.name,
          description: values.description
        };
        const response = await dispatch(aiDispositionUpdate(payload)).unwrap();
        if (response && response.success) {
          toast.success(response.message || 'Record Updated Successfully');
          onCloseClick(true);
          resetForm();
        }
      } else {
        const payload = {
          bot_id: values.bot_id,
          name: values.name,
          description: values.description
        };
        const response = await dispatch(aiDispositionAdd(payload)).unwrap();
        if (response && response.success) {
          toast.success(response.message || 'Record Added Successfully');
          onCloseClick(true);
          resetForm();
        }
      }
    } catch (error) {
      console.error('error', error);
    }
  };

  const { values, handleChange, handleBlur, handleSubmit, touched, errors, resetForm, setValues } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    });

  const getBotOptions = async () => {
    try {
      const response = await dispatch(callexAiAgentOptionsGet()).unwrap();
      if (response && response.data) {
        setBotOptions(response.data.map((val) => ({ label: val.name, value: val.id })));
      } else {
        setBotOptions([]);
      }
    } catch (error) {
      setBotOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBotOptions();
  }, []);

  useEffect(() => {
    if (isEdit && editRecord) {
      setValues({
        bot_id: editRecord.bot_id ?? '',
        name: editRecord.name || '',
        description: editRecord.description || ''
      });
    } else {
      resetForm();
    }
  }, [isEdit, editRecord, isOpen]);

  return (
    <Modal isOpen={isOpen} size="lg" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit AI Disposition' : 'Add AI Disposition'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="ai-disposition-form" onSubmit={handleSubmit}>
          <Row className="mt-3">
            <Col lg="6">
              <FormSelect
                name="bot_id"
                label="Bot"
                placeholder="Select Bot"
                value={values.bot_id}
                options={botOptions}
                disabled={isEdit}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="6">
              <FormInput
                name="name"
                label="Name"
                placeholder="Enter Name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col lg="12">
              <FormTextarea
                name="description"
                label="Description"
                placeholder="Enter Description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
          </Row>
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="ai-disposition-form" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditAIDispositionModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditAIDispositionModal;
